import { useState, useEffect, useRef } from 'react';
import { Checkbox } from '@/shared/components/Checkbox';
import {
  OutlinedDropdown,
  type OutlinedDropdownOption,
} from '@/shared/components/OutlinedDropdown';
import { ToggleButton } from '@/shared/components/ToggleButton';
import type { AdminPrivileges } from '../types/adminPrivilegeTypes';
import {
  ADMIN_MODULES,
  type ModuleConfig,
  tAdmin,
  showAdminLangToggle,
} from '../constants/adminPrivilegeConstants';
import { applyAdminPrivilegeCascade } from '../utils/adminPrivilegeCascade';

type PrivilegeKey = keyof AdminPrivileges;

function moduleToOptions(
  privileges: AdminPrivileges,
  module: ModuleConfig,
  translate: (key: string) => string
): OutlinedDropdownOption[] {
  return module.items.map((item) => ({
    value: item.key,
    label: translate(item.label),
    checked: privileges[item.key],
  }));
}

function getAllState(privileges: AdminPrivileges): {
  checked: boolean;
  indeterminate: boolean;
} {
  const allKeys = Object.keys(privileges) as PrivilegeKey[];
  const checkedCount = allKeys.filter((k) => privileges[k]).length;
  return {
    checked: checkedCount === allKeys.length,
    indeterminate: checkedCount > 0 && checkedCount < allKeys.length,
  };
}

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={`transition-transform duration-200 shrink-0 ${open ? '' : '-rotate-90'}`}
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="#474A4C"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface AdminPrivilegesSectionProps {
  privileges: AdminPrivileges;
  onPrivilegesChange: (privileges: AdminPrivileges) => void;
  disabled?: boolean;
  privilegesError?: string;
}

const AdminPrivilegesSection = ({
  privileges,
  onPrivilegesChange,
  disabled = false,
  privilegesError,
}: AdminPrivilegesSectionProps) => {
  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const [isPolish, setIsPolish] = useState(true);
  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (privilegesError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [privilegesError]);

  const translate = (key: string) => (isPolish ? tAdmin(key) : key);

  const handleModuleChange = (
    module: ModuleConfig,
    newOptions: OutlinedDropdownOption[]
  ) => {
    // Use identity translate for prev-options comparison — labels don't affect checked state
    const prevOptions = moduleToOptions(privileges, module, (k) => k);
    let next = { ...privileges };
    newOptions.forEach((newOpt, i) => {
      const prevOpt = prevOptions[i];
      if (prevOpt && newOpt.checked !== prevOpt.checked) {
        const key = module.items[i]?.key;
        if (key !== undefined) {
          next = applyAdminPrivilegeCascade(next, key, newOpt.checked ?? false);
        }
      }
    });
    onPrivilegesChange(next);
  };

  const handleSelectAll = (value: boolean) => {
    const next = { ...privileges };
    (Object.keys(next) as PrivilegeKey[]).forEach((k) => {
      next[k] = value;
    });
    onPrivilegesChange(next);
  };

  const allState = getAllState(privileges);

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setIsSectionOpen((prev) => !prev)}
        className="flex items-center justify-between w-full text-left cursor-pointer"
      >
        <p className="text-[18px] text-on-surface-variant">Uprawnienia</p>
        <div className="flex items-center gap-2">
          {showAdminLangToggle && (
            <div role="presentation" onClick={(e) => e.stopPropagation()}>
              <ToggleButton
                checked={isPolish}
                onChange={setIsPolish}
                onText="PL"
                offText="EN"
                label={isPolish ? 'PL' : 'EN'}
              />
            </div>
          )}
          <ChevronIcon open={isSectionOpen} />
        </div>
      </button>

      {privilegesError && (
        <p ref={errorRef} className="text-xs text-error-40">
          {privilegesError}
        </p>
      )}

      {isSectionOpen && (
        <div className="flex flex-col gap-4">
          <Checkbox
            label={translate('Select All')}
            checked={allState.checked}
            indeterminate={allState.indeterminate}
            onChange={handleSelectAll}
            disabled={disabled}
            testId="privileges-select-all"
          />

          <div className="flex flex-wrap gap-4 items-start w-full">
            {ADMIN_MODULES.map((module) => (
              <div
                key={module.id}
                className="w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]"
                data-testid={`privilege-module-${module.id}`}
              >
                <OutlinedDropdown
                  title={translate(module.title)}
                  options={moduleToOptions(privileges, module, translate)}
                  onChange={(updated) => handleModuleChange(module, updated)}
                  disabled={disabled}
                  testId={module.id}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPrivilegesSection;
